import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../client";
import { PLATFORM_ACCOUNTS_KEY } from "./usePlatformAccounts";

/** Scr.Enums.CustomerMembershipStatus, for the states a request is listed in. */
export const MembershipRequestStatus = {
  PendingApproval: 1,
  Declined: 3,
} as const;

const MEMBERSHIP_REQUESTS_KEY = ["membership-requests"] as const;

export interface MembershipRequestDto {
  id: string;
  membershipTierId: string;
  tierName: string;
  platformId: string;
  platformName: string;
  badgeColour: string;
  badgeLetters: string | null;
  accountPrompt: string | null;
  platformAccount: string;
  status: number;
  requestedAt: string;
  decidedAt: string | null;
  /** Staff's reason, written for the customer. */
  declineReason: string | null;
}

interface ListMembershipRequestsResponse {
  items: MembershipRequestDto[];
}

/** Requests awaiting approval or declined, the newest per tier. An approved one shows as a membership. */
export function useMyMembershipRequests() {
  return useQuery<MembershipRequestDto[]>({
    queryKey: MEMBERSHIP_REQUESTS_KEY,
    queryFn: async () => {
      const res = await apiClient.get<ListMembershipRequestsResponse>("/customers/me/membership-requests");
      return res.data.items;
    },
  });
}

export function useRequestMembership() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: { membershipTierId: string; platformAccount: string }) => {
      const res = await apiClient.post<{ id: string }>("/customers/me/membership-requests", request);
      return res.data;
    },
    // Sending one also keeps the account on the customer's profile.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: MEMBERSHIP_REQUESTS_KEY }),
        queryClient.invalidateQueries({ queryKey: PLATFORM_ACCOUNTS_KEY }),
      ]),
  });
}
